/**
 * The offer card that sits under the talk.
 *
 * One card for all five kinds the host can set: a call, a checkout, a code, a
 * file or a note. The watch page draws it for the viewer and the board's During
 * the talk stage draws the same thing for the host, so what is promised on the
 * board is what lands in the room.
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { isCustomLook } from "../../shared/offer";
import type { OfferJson } from "../../shared/types";
import { AttVideo } from "../components/MessageAttachments";
import { assetUrl } from "../lib/api";
import { luminanceOf } from "./brandVars";
import "../styles/watch.css";

/** The box's classes and colour for its look; no look is the page's own accent. */
export function offerLook(look: string | undefined): { className: string; style?: CSSProperties } {
  if (!look) return { className: "" };
  if (isCustomLook(look)) {
    const light = (luminanceOf(look) ?? 0) > 0.45;
    return {
      className: light ? "w-look-custom w-look-light" : "w-look-custom",
      style: { "--o-custom": look } as CSSProperties,
    };
  }
  return { className: `w-look-${look}` };
}

/** How long the code button reads "Copied" after a press. */
const COPIED_MS = 2000;

function firstName(full: string): string {
  return full.trim().split(/\s+/)[0] || full;
}

/** Where a code is used, in the words a person reads: the site's own name. */
function siteOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

/** An uploaded file is an asset key; a host who pasted a link keeps their link. */
export function fileHref(file: string): string {
  return /^https?:\/\//.test(file) ? file : assetUrl(file);
}

/**
 * The offer link with the viewer's own address on it, so a sign-up on the host's
 * product can be matched back to the person. Any query the link already has is
 * kept. Only a call and a checkout carry it: they are the two that end on the
 * host's own page.
 */
export function withEmail(url: string, email: string): string {
  if (!email.trim()) return url;
  const at = url.indexOf("#");
  const hash = at >= 0 ? url.slice(at) : "";
  const head = at >= 0 ? url.slice(0, at) : url;
  const join = head.includes("?") ? "&" : "?";
  return `${head}${join}email=${encodeURIComponent(email)}${hash}`;
}

const IcArrow = (): ReactNode => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M7 17 17 7M9 7h8v8" />
  </svg>
);
const IcShield = (): ReactNode => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l7 3v6c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6Z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);
const IcSave = (): ReactNode => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
  </svg>
);

/** The line under the heading, in the words each kind wants. */
function subLine(offer: OfferJson, host: string): string {
  switch (offer.kind) {
    case "call":
      return `Pick a time that suits you and ${host} takes it from there.`;
    case "buy":
      return "Everything you need is on the next page.";
    case "code":
      return "Here is your code.";
    default:
      return "Here is the file from the talk.";
  }
}

/** The small reassurance beside the button. */
function trustLine(offer: OfferJson, host: string): string {
  switch (offer.kind) {
    case "call":
      return `No pressure. Opens ${host}'s booking page.`;
    case "buy":
      return "It opens in a new tab.";
    case "code": {
      const site = offer.url ? siteOf(offer.url) : "";
      return site ? `Use it at ${site}.` : "Use it when you check out.";
    }
    default:
      return "Yours to keep.";
  }
}

export function WatchOffer({
  offer,
  hostName,
  viewerName,
  viewerEmail = "",
  onConvert,
  onVideo,
  compact = false,
}: {
  offer: OfferJson;
  hostName: string;
  /** The viewer's name; the card greets them by their first one. Empty on the
   *  board's stage, where there is nobody to greet yet. */
  viewerName: string;
  /** The viewer's address, carried to the host's product on a call or a checkout. */
  viewerEmail?: string;
  /** The press that counts as the conversion, for every kind that has one. */
  onConvert?: () => void;
  /** The note started, or played to the end. */
  onVideo?: (state: "play" | "end") => void;
  /** The rail's version: the kicker, the label and the button, nothing else. */
  compact?: boolean;
}): ReactNode {
  const [copied, setCopied] = useState(false);
  const back = useRef(0);
  useEffect(() => () => window.clearTimeout(back.current), []);

  const host = firstName(hostName);
  const you = viewerName.trim() ? firstName(viewerName) : "";
  const kind = offer.kind;
  const label = offer.label || "Book a call";

  const copy = () => {
    onConvert?.();
    setCopied(true);
    window.clearTimeout(back.current);
    back.current = window.setTimeout(() => setCopied(false), COPIED_MS);
    try {
      void navigator.clipboard?.writeText(offer.code ?? "");
    } catch {
      /* a browser that will not hand over the clipboard still shows the code */
    }
  };

  const look = offerLook(offer.look);
  const video = !compact && offer.video ? offer.video : null;
  const classes = ["w-card", "w-offer", compact ? "w-compact" : "", video ? "w-beside" : "", look.className]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={classes} style={look.style}>
      <div className="w-glow" />
      <div className="w-glow2" />
      {video ? (
        <div className="w-ovideo">
          <AttVideo
            src={video.url}
            poster={video.poster ?? undefined}
            label={`A note from ${host}`}
            duration={video.duration_sec}
            download={false}
            onStart={() => onVideo?.("play")}
            onFinish={() => onVideo?.("end")}
          />
        </div>
      ) : null}
      <div className="w-oin">
        <span className="w-okick"><span className="w-kbrass" />A note from <bdi>{host}</bdi></span>
        {kind === "message" ? (
          <p className="w-onote">{offer.text}</p>
        ) : (
          <>
            {compact ? null : (
              <>
                <h2>Ready for the next step{you ? <>, <em>{you}</em></> : null}?</h2>
                <p className="w-osub">{subLine(offer, host)}</p>
              </>
            )}
            {kind === "code" ? (
              <div className="w-ocode">
                <span className="w-ocodev w-num">{offer.code}</span>
                <button
                  type="button"
                  className={copied ? "w-btn w-ocopy w-copied" : "w-btn w-ocopy"}
                  onClick={copy}
                >
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
            ) : null}
            <div className="w-offerfoot">
              {kind === "download" && offer.file ? (
                <a
                  className="w-btn"
                  href={fileHref(offer.file)}
                  download
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => onConvert?.()}
                >
                  {label}
                  <IcSave />
                </a>
              ) : null}
              {(kind === "call" || kind === "buy") && offer.url ? (
                <a
                  className="w-btn"
                  href={withEmail(offer.url, viewerEmail)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => onConvert?.()}
                >
                  {label}
                  <IcArrow />
                </a>
              ) : null}
              {compact ? null : (
                <span className="w-otrust">
                  <IcShield />
                  {trustLine(offer, host)}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
